import React, { useState } from 'react';
import { useArticles } from '../hooks/useArticles';

const AddUrlForm = ({ onSuccess, className = '' }) => {
  const [url, setUrl] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [bulkResults, setBulkResults] = useState(null);

  const { addArticle, addArticlesBulk, isCreating, error, clearError } = useArticles();

  const validateUrl = (urlString) => {
    try {
      const urlObj = new URL(urlString);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const parseUrls = (input) => {
    // Split by newlines and filter empty lines
    return input
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
  };

  const validateUrls = (urls) => {
    const results = urls.map(url => ({
      url,
      valid: validateUrl(url)
    }));
    return results;
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setUrl(value);

    // Clear errors and results when user starts typing
    if (error) clearError();
    if (validationError) setValidationError('');
    if (bulkResults) setBulkResults(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!url.trim()) {
      setValidationError('Please enter a URL');
      return;
    }

    const urls = parseUrls(url);

    // Single URL case - use existing flow
    if (urls.length === 1) {
      if (!validateUrl(urls[0])) {
        setValidationError('Please enter a valid URL (must start with http:// or https://)');
        return;
      }

      try {
        const article = await addArticle({ url: urls[0] });

        // Reset form
        setUrl('');
        setIsExpanded(false);
        setValidationError('');

        // Notify parent component
        if (onSuccess) {
          onSuccess(article);
        }
      } catch (error) {
        console.error('Error adding article:', error);
      }
      return;
    }

    // Multiple URLs - validate and use bulk endpoint
    const validationResults = validateUrls(urls);
    const validUrls = validationResults.filter(r => r.valid).map(r => r.url);
    const invalidUrls = validationResults.filter(r => !r.valid).map(r => r.url);

    if (validUrls.length === 0) {
      setValidationError('No valid URLs found. URLs must start with http:// or https://');
      return;
    }

    try {
      const response = await addArticlesBulk(validUrls);

      // Store results for display
      setBulkResults({
        ...response,
        invalidUrls
      });

      // Reset form after a delay to show results
      setTimeout(() => {
        setUrl('');
        setIsExpanded(false);
        setValidationError('');
        setBulkResults(null);

        // Notify parent component with first successful article
        if (onSuccess && response.successful > 0) {
          const firstSuccess = response.results.find(r => r.success);
          if (firstSuccess) {
            onSuccess(firstSuccess.article);
          }
        }
      }, 3000);
    } catch (error) {
      console.error('Error adding articles in bulk:', error);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsExpanded(false);
      setUrl('');
      setValidationError('');
      if (error) clearError();
    }
  };

  const handleFocus = () => {
    setIsExpanded(true);
  };

  const handleBlur = () => {
    // Only collapse if we're not submitting and there's no content
    if (!isCreating && !url.trim() && !validationError && !error && !bulkResults) {
      setIsExpanded(false);
    }
  };

  // Count URLs in input
  const urlCount = url.trim() ? parseUrls(url).length : 0;

  return (
    <div className={`w-full ${className}`}>
      <form onSubmit={handleSubmit} className="w-full">
        <div className="relative">
          <div className={`transition-all duration-200 ${
            isExpanded ? 'bg-white rounded-lg shadow-lg border border-gray-200 p-4' : 'bg-gray-50 rounded-lg border border-gray-300 hover:border-gray-400'
          }`}>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 pt-2">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                  />
                </svg>
              </div>

              <div className="flex-1">
                {isExpanded ? (
                  <textarea
                    placeholder="Paste URLs to save articles (one per line)..."
                    value={url}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    disabled={isCreating}
                    rows={urlCount > 1 ? Math.min(urlCount, 5) : 3}
                    className={`w-full px-0 py-2 bg-transparent border-none focus:outline-none focus:ring-0 placeholder-gray-500 text-gray-900 resize-none ${
                      isCreating ? 'cursor-not-allowed opacity-50' : ''
                    }`}
                  />
                ) : (
                  <input
                    type="text"
                    placeholder="Paste a URL to save an article..."
                    value={url}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    disabled={isCreating}
                    className={`w-full px-0 py-2 bg-transparent border-none focus:outline-none focus:ring-0 placeholder-gray-500 text-gray-900 ${
                      isCreating ? 'cursor-not-allowed opacity-50' : ''
                    }`}
                  />
                )}
              </div>
              
              {isExpanded && (
                <div className="flex items-center space-x-2">
                  <button
                    type="submit"
                    disabled={isCreating || !url.trim()}
                    className="flex-shrink-0 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  >
                    {isCreating ? (
                      <div className="flex items-center space-x-2">
                        <div className="spinner w-4 h-4"></div>
                        <span>Saving...</span>
                      </div>
                    ) : urlCount > 1 ? (
                      `Save ${urlCount} Articles`
                    ) : (
                      'Save'
                    )}
                  </button>
                </div>
              )}
            </div>

            {isExpanded && bulkResults && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <div className="text-sm font-medium text-blue-900 mb-1">
                  Bulk Operation Results
                </div>
                <div className="text-xs text-blue-700 space-y-1">
                  <div>✓ {bulkResults.successful} successful</div>
                  {bulkResults.duplicates > 0 && (
                    <div>⚠ {bulkResults.duplicates} duplicates skipped</div>
                  )}
                  {bulkResults.failed > 0 && (
                    <div>✗ {bulkResults.failed} failed</div>
                  )}
                  {bulkResults.invalidUrls && bulkResults.invalidUrls.length > 0 && (
                    <div>✗ {bulkResults.invalidUrls.length} invalid URLs</div>
                  )}
                </div>
              </div>
            )}

            {isExpanded && (validationError || error) && (
              <div className="mt-2 text-sm text-red-600">
                {validationError || error}
              </div>
            )}

            {isExpanded && !validationError && !error && !bulkResults && urlCount > 0 && (
              <div className="mt-2 text-xs text-gray-500">
                {urlCount > 1 ? (
                  <>Found {urlCount} URLs • Press Escape to cancel</>
                ) : (
                  <>Press Escape to cancel • <span className="font-medium">Processing happens in the background</span></>
                )}
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddUrlForm;